import * as React from "react";
import { observer, inject } from "mobx-react";
import { Link } from "react-router-dom";
import { UncontrolledTooltip } from "reactstrap";

import { HorseForSale, HSTExemptionType, HammerFallOutcome } from "../../model/HorseForSale";
import { FinalBidAmountWarning } from "../common/FinalBidAmountWarning";

import { Customer, ExtractFriendlyName } from "src/model/Customer";
import { EditableHorseForSalePurchase, CheckoutTabs } from "./CheckoutTemplate";
import { CustomerEndpoints } from "src/endpoints/CustomerEndpoints";

import { CheckoutHorseHeader } from "./CheckoutHorsesHeader";
import { SortFn } from "./CommonTableSorting";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrophy, faExternalLinkSquareAlt, faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { CustomerLinkCheckout } from "../common/CustomerLinkCheckout";

export const HSTOptionsFriendlyName = (option: HSTExemptionType): string => {
    switch (option) {
        case HSTExemptionType.NONE:
            return "No";
        case HSTExemptionType.INTL_SHIP:
            return "Yes - US Resident, horse delivered to the US";
        case HSTExemptionType.FN:
            return "Yes - Aboriginal Status";
        default:
            return "?";
    }
};

export interface Props {
    customerEndpoints?: CustomerEndpoints;
    currentHorseForSale: HorseForSale;

    winningBidder: Customer;
    // No  currentHorseForSale here?

    onChange: (currentHorseForSale: HorseForSale, purchasers: EditableHorseForSalePurchase.Fields) => void;
    horseForSalePurchaseList: EditableHorseForSalePurchase.Fields;

    goToTab: (currentTab: string) => void;
    nextHorse: () => void;
    showGoBackHorse: boolean;
    showGoToNextHorse: boolean;
    submitHorseForSaleCheckout: () => void;
}
export interface State {
    horseForSalePurchaseList: Array<EditableHorseForSalePurchase.PurchaseFields>;
}
@inject("customerEndpoints")
@observer
export default class CheckoutHSTExemption extends React.Component<Props, State> {
    isWinnerBidderAnAgentAuthorizationForThisPurchaser = (purchaser: Customer): boolean => {
        const winnerBidderId = this.props.winningBidder!.id;
        const currentHorseForSaleSaleId = this.props.currentHorseForSale.sale.id;

        return !!(
            purchaser.id !== winnerBidderId &&
            purchaser.delegatorAuthorizations.find(
                aa => aa.agent.id === winnerBidderId && aa.sale.id === currentHorseForSaleSaleId && aa.dateRevoked === null
            )
        );
    };

    handleChangeHSTExemption = (row: EditableHorseForSalePurchase.PurchaseFields) => (
        e: React.ChangeEvent<HTMLSelectElement | HTMLTextAreaElement | HTMLInputElement>
    ) => {
        e.preventDefault();
        const { name, value } = e.target;
        const { currentHorseForSale, horseForSalePurchaseList } = this.props;
        const purchase = JSON.parse(
            JSON.stringify(horseForSalePurchaseList.buyers.find(hfsp => hfsp.purchaser!.id === row.purchaser!.id)!)
        ) as EditableHorseForSalePurchase.PurchaseFields;

        purchase.hstExemption = {
            exemption: name === "hstExempt" ? (value as HSTExemptionType) : purchase.hstExemption.exemption,
            notes: name === "notes" ? e.target.value : purchase.hstExemption.notes,
            firstNationsIdNumber: name === "firstNationsIdNumber" ? e.target.value : purchase.hstExemption.firstNationsIdNumber
        };

        const filteredPurchasers = horseForSalePurchaseList.buyers.filter(hfsp => hfsp.purchaser!.id !== row.purchaser!.id);
        this.props.onChange(currentHorseForSale, {
            ...this.props.horseForSalePurchaseList,
            buyers: [...filteredPurchasers, EditableHorseForSalePurchase.PurchaseMakeCopy(purchase)]
        });
    };

    renderPurchaser = (p: Customer) => {
        if (this.isWinnerBidderAnAgentAuthorizationForThisPurchaser(p)) {
            return <CustomerLinkCheckout page="customer-account" customer={p} notHaveAgentAuthorization={true} />;
        } else if (p.id === this.props.winningBidder.id) {
            return <CustomerLinkCheckout page="customer-account" customer={p} isWinningBider={true} />;
        } else {
            return <CustomerLinkCheckout page="customer-account" customer={p} notHaveAgentAuthorization={true} />;
        }
    };

    render() {
        const { currentHorseForSale, horseForSalePurchaseList } = this.props;
        const finalBidAmount = (currentHorseForSale.outcome as HammerFallOutcome).finalBidAmount / 100;
        return (
            <div className="card shadow">
                <div className="card-header">
                    <CheckoutHorseHeader stepNumber={3} stepTitle="HST Exemption" currentHorseForSale={currentHorseForSale} />
                </div>
                <div className="card-body">
                    <FinalBidAmountWarning finalBidAmount={finalBidAmount} />
                    <div className="form-row my-3">
                        <div className="col-12">
                            <div className="table-responsive">
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th>Buyer(s)</th>
                                            <th>HST Exemption</th>
                                            <th>Status Card ID#</th>
                                            <th>Notes</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {horseForSalePurchaseList.buyers
                                            .sort(SortFn(this.props.winningBidder, this.props.currentHorseForSale.sale.id))
                                            .map(hfsp => {
                                                const value = hfsp.hstExemption.exemption;
                                                const isPurchaserWinningBidder = this.props.winningBidder.id === hfsp.purchaser!.id;
                                                const isNotAgentAuthorizationOf = !this.isWinnerBidderAnAgentAuthorizationForThisPurchaser(
                                                    hfsp.purchaser!
                                                );
                                                const handleTdClass =
                                                    isPurchaserWinningBidder || isNotAgentAuthorizationOf
                                                        ? "table-bordered-td-bg" + " " + (isPurchaserWinningBidder ? `text-info` : ``)
                                                        : "";
                                                return (
                                                    <tr key={hfsp.purchaser!.id}>
                                                        <td className={handleTdClass}>{hfsp.purchaser && this.renderPurchaser(hfsp.purchaser)}</td>
                                                        <td className={handleTdClass}>
                                                            <select
                                                                className="form-control not-required"
                                                                name="hstExempt"
                                                                onChange={this.handleChangeHSTExemption(hfsp)}
                                                                value={value}
                                                            >
                                                                {Object.keys(HSTExemptionType).map(option => (
                                                                    <option key={option} value={option}>
                                                                        {HSTOptionsFriendlyName(option as HSTExemptionType)}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            {value === HSTExemptionType.INTL_SHIP && (
                                                                <div className="alert alert-warning mt-3 d-flex">
                                                                    <FontAwesomeIcon icon={faExclamationTriangle} className="mr-3 mt-1" />
                                                                    <span>
                                                                        <strong className="font-weight-bold">
                                                                            Official bill of lading from a carrier is required.
                                                                        </strong>{" "}
                                                                        Do not proceed with checkout without collecting documentation first.
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className={handleTdClass}>
                                                            {value === HSTExemptionType.FN ? (
                                                                <input
                                                                    className="form-control not-required"
                                                                    type="text"
                                                                    name="firstNationsIdNumber"
                                                                    value={
                                                                        hfsp.hstExemption.firstNationsIdNumber ||
                                                                        hfsp.purchaser!.firstNationsIdNumber ||
                                                                        ""
                                                                    }
                                                                    onChange={this.handleChangeHSTExemption(hfsp)}
                                                                />
                                                            ) : (
                                                                "-"
                                                            )}
                                                        </td>
                                                        <td className={handleTdClass}>
                                                            <textarea
                                                                name="notes"
                                                                className="form-control not-required"
                                                                rows={2}
                                                                placeholder=""
                                                                style={{ resize: "none" }}
                                                                onChange={this.handleChangeHSTExemption(hfsp)}
                                                                value={(hfsp.hstExemption && hfsp.hstExemption.notes) || ""}
                                                            />
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                    <div className="for-row text-right">
                        <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                this.props.goToTab(CheckoutTabs.CREDIT_AND_INSURANCE);
                            }}
                        >
                            Back to Credit &amp; Insurance
                        </button>
                        <button
                            form="agentAuthorization"
                            className="btn btn-primary btn-sm mr-0 ml-3"
                            onClick={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                this.props.submitHorseForSaleCheckout();
                            }}
                        >
                            Generate Invoices and {this.props.showGoToNextHorse ? `Go to Next Horse` : `Go to Step 4`}
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/checkout/CheckoutHSTExemption.tsx